import React, { useEffect, useRef, useState } from "react";
import HeartLoader from "@/components/HeartLoader";
import { motion } from "framer-motion";
import {
  HardDrive, Upload, Loader2, Search as SearchIcon, Download, Share2, Trash2, FolderArchive,
  Folder, FileText, Sparkles, X, Copy, Check, Eye, Mail,
} from "lucide-react";
import { api, apiError, API, authToken } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

const humanSize = (b) => {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB"]; let i = 0; let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};

async function blobDownload(url, fallbackName) {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${authToken.get()}` }, credentials: "include" });
  if (!res.ok) throw new Error("download failed");
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename="?([^"]+)"?/);
  const name = m ? m[1] : fallbackName;
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href; a.download = name; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(href);
}

export default function SmartDrive() {
  const { user, setUser } = useApp();
  const [space, setSpace] = useState("personal");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [overview, setOverview] = useState("");
  const [shareFor, setShareFor] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/drive/list?space=${space}`);
      setData(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { api.get("/drive/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, []);
  useEffect(() => { load(); setOverview(""); setResults(null); }, [space]);

  const onPick = () => fileRef.current?.click();

  const doUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("space", space);
    try {
      const { data } = await api.post("/drive/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.credits != null && setUser) setUser((u) => (u ? { ...u, credits: data.credits } : u));
      const skipped = (data.skipped || []).length;
      toast.success(`${data.count} file${data.count === 1 ? "" : "s"} filed away${data.charged ? ` · ${data.charged} credits` : " · free"}${skipped ? ` · ${skipped} skipped` : ""}`);
      load();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const onFile = (e) => { doUpload(e.target.files); e.target.value = ""; };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) doUpload(e.dataTransfer.files);
  };

  const runSearch = async (e) => {
    e?.preventDefault();
    if (!q.trim()) { setResults(null); return; }
    try { const { data } = await api.get(`/drive/search?q=${encodeURIComponent(q.trim())}`); setResults(data.items || []); }
    catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const getOverview = async () => {
    setOverview("…");
    try { const { data } = await api.get(`/drive/overview?space=${space}`); setOverview(data.overview || "No read yet — add a few files."); }
    catch { setOverview("Couldn't read your drive just now."); }
  };

  const del = async (f) => {
    if (!window.confirm(`Delete "${f.filename}"? This can't be undone.`)) return;
    try { await api.delete(`/drive/file/${f.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const dl = async (f, mode) => {
    try { await blobDownload(`${API}/drive/file/${f.id}/download?mode=${mode}`, f.filename); }
    catch { toast.error("Couldn't download. Try again."); }
  };

  const zipFolder = async (topic) => {
    try { await blobDownload(`${API}/drive/zip?space=${space}${topic ? `&topic=${encodeURIComponent(topic)}` : ""}`, `${space}.zip`); }
    catch { toast.error("Couldn't build the ZIP."); }
  };

  const tree = data?.tree || {};
  const topics = Object.keys(tree);

  if (user === undefined) return <div className="min-h-screen grid place-items-center text-sec"><HeartLoader /></div>;

  return (
    <div className="max-w-6xl mx-auto px-5 pt-24 pb-16 relative" data-testid="smart-drive-page"
      onDragOver={(e) => { e.preventDefault(); if (!dragActive) setDragActive(true); }}
      onDragLeave={(e) => { e.preventDefault(); if (e.currentTarget === e.target) setDragActive(false); }}
      onDrop={onDrop}>
      {dragActive && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center pointer-events-none" data-testid="drive-dropzone">
          <div className="rounded-2xl border-2 border-dashed border-brand bg-brand/10 px-10 py-8 text-center">
            <Upload size={34} className="mx-auto text-brand mb-2" />
            <p className="text-lg font-medium">Drop your files to upload</p>
            <p className="text-sec text-sm">They'll be auto-filed into your {space} drive</p>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2"><HardDrive className="text-brand" /><h1 className="font-serif text-4xl tracking-tight">Smart Drive</h1></div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" hidden multiple onChange={onFile} data-testid="drive-file-input" />
          <button data-testid="drive-upload-btn" onClick={onPick} disabled={uploading} className="btn-primary rounded-full h-10 px-5 text-sm flex items-center gap-2 disabled:opacity-60">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {uploading ? "Filing them away…" : "Upload files"}
          </button>
        </div>
      </div>
      <p className="text-sec text-sm mt-2 max-w-2xl">Drop in any files (or drag &amp; drop anywhere here) — My Kind auto-files them into the right folder, reads what they are, and keeps them safe &amp; compressed. Search, preview, download or share anytime. {meta ? <span className="text-brand">~{meta.per_100mb_credits?.personal} cr/100MB personal · {meta.per_100mb_credits?.professional} cr/100MB professional (region-fair).</span> : null}</p>

      {/* Space switcher */}
      <div className="flex items-center gap-2 mt-5" data-testid="drive-spaces">
        {["personal", "professional"].map((s) => (
          <button key={s} data-testid={`drive-space-${s}`} onClick={() => setSpace(s)}
            className={`h-9 px-4 rounded-full text-sm capitalize transition-colors ${space === s ? "bg-brand text-black font-medium" : "rud-card text-sec"}`}>
            {s}{data?.space_counts?.[s] ? ` · ${data.space_counts[s]}` : ""}
          </button>
        ))}
        {topics.length > 0 && (
          <button data-testid="drive-zip-space" onClick={() => zipFolder("")} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5 ml-auto"><FolderArchive size={14} /> Download all (ZIP)</button>
        )}
      </div>

      {/* Search + AI overview */}
      <div className="grid md:grid-cols-2 gap-3 mt-4">
        <form onSubmit={runSearch} className="flex items-center gap-2 border border-rud bg-elevated rounded-xl px-3 py-2" data-testid="drive-search">
          <SearchIcon size={16} className="text-sec" />
          <input data-testid="drive-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search files, folders & what's inside…" className="bg-transparent flex-1 text-sm outline-none" />
          {results != null && <button type="button" onClick={() => { setQ(""); setResults(null); }} className="text-sec"><X size={15} /></button>}
        </form>
        <button data-testid="drive-overview-btn" onClick={getOverview} className="rud-card rounded-xl px-4 py-2 text-sm flex items-center gap-2 text-left hover:border-brand/50 transition-colors">
          <Sparkles size={15} className="text-brand shrink-0" /> <span className="truncate">{overview && overview !== "…" ? overview : "Read my whole drive — what do I keep here?"}</span>
          {overview === "…" && <Loader2 size={14} className="animate-spin ml-auto" />}
        </button>
      </div>
      {overview && overview !== "…" && <div className="mt-3 rounded-xl border border-brand/25 bg-brand/5 p-3 text-sm text-sec" data-testid="drive-overview">{overview}</div>}

      {/* Search results */}
      {results != null ? (
        <div className="mt-6" data-testid="drive-search-results">
          <div className="text-sm text-sec mb-2">{results.length} result{results.length === 1 ? "" : "s"}</div>
          <div className="space-y-2">{results.map((f) => <FileRow key={f.id} f={f} onDl={dl} onShare={setShareFor} onDel={del} />)}</div>
        </div>
      ) : loading ? (
        <div className="flex items-center gap-2 text-sec mt-10"><Loader2 className="animate-spin" size={18} /> Opening your drive…</div>
      ) : topics.length === 0 ? (
        <div className="mt-10 text-center rud-card rounded-2xl p-10" data-testid="drive-empty">
          <Folder size={34} className="mx-auto text-brand mb-3" />
          <p className="text-sec text-sm">Your {space} drive is empty. Upload your first file and I'll organise it for you. ✨</p>
        </div>
      ) : (
        <div className="mt-6 space-y-6" data-testid="drive-tree">
          {topics.map((topic) => {
            const node = tree[topic];
            const subs = Object.keys(node.subfolders || {});
            return (
              <div key={topic} data-testid={`drive-topic-${topic}`} className="rud-card rounded-2xl p-4">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2"><Folder size={17} className="text-brand" /><h3 className="font-medium">{topic}</h3></div>
                  <button onClick={() => zipFolder(topic)} className="text-xs text-sec hover:text-brand flex items-center gap-1"><FolderArchive size={13} /> ZIP</button>
                </div>
                <div className="space-y-2">
                  {(node.files || []).map((f) => <FileRow key={f.id} f={f} onDl={dl} onShare={setShareFor} onDel={del} />)}
                  {subs.map((sub) => (
                    <div key={sub} className="ml-2 pl-3 border-l border-rud">
                      <div className="text-xs text-sec flex items-center gap-1.5 my-1"><Folder size={12} /> {sub}</div>
                      <div className="space-y-2">{node.subfolders[sub].map((f) => <FileRow key={f.id} f={f} onDl={dl} onShare={setShareFor} onDel={del} />)}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {shareFor && <ShareModal file={shareFor} onClose={() => setShareFor(null)} />}
    </div>
  );
}

function FileRow({ f, onDl, onShare, onDel }) {
  return (
    <div data-testid={`drive-file-${f.id}`} className="flex items-center gap-3 rounded-xl border border-rud bg-white/5 px-3 py-2.5">
      <FileText size={16} className="text-sec shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm truncate">{f.filename}</div>
        <div className="text-[11px] text-sec truncate">{f.ai_kind ? `${f.ai_kind} · ` : ""}{f.ai_summary || "—"}</div>
      </div>
      <div className="text-[11px] text-sec hidden sm:block shrink-0">{humanSize(f.size)}{f.compressed ? " · zipped" : ""}</div>
      <button data-testid={`drive-dl-${f.id}`} title="Download" onClick={() => onDl(f, "original")} className="text-sec hover:text-brand p-1"><Download size={15} /></button>
      <button data-testid={`drive-share-${f.id}`} title="Share" onClick={() => onShare(f)} className="text-sec hover:text-brand p-1"><Share2 size={15} /></button>
      <button data-testid={`drive-del-${f.id}`} title="Delete" onClick={() => onDel(f)} className="text-sec hover:text-red-400 p-1"><Trash2 size={15} /></button>
    </div>
  );
}

function ShareModal({ file, onClose }) {
  const [perm, setPerm] = useState("view");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState("");

  const makeLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/drive/file/${file.id}/share`, { permission: perm });
      setLink(window.location.origin + data.path);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const sendEmail = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/drive/file/${file.id}/email`, { to: email.trim(), permission: perm }); toast.success(data.email_sent ? `Emailed to ${email.trim()} 🙏` : "Share link created (email sends once domain is verified)"); setEmail(""); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="drive-share-modal">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rud-card rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1"><h3 className="font-medium flex items-center gap-2"><Share2 size={16} className="text-brand" /> Share “{file.filename}”</h3><button onClick={onClose}><X size={17} /></button></div>
        <p className="text-[12px] text-sec mb-3">The person will be asked to sign in to open it.</p>
        <div className="flex gap-2 mb-3" data-testid="share-permission">
          {[["view", Eye], ["download", Download], ["edit", FileText]].map(([p, Icon]) => (
            <button key={p} data-testid={`share-perm-${p}`} onClick={() => setPerm(p)} className={`flex-1 rounded-lg px-3 py-2 text-xs capitalize flex items-center justify-center gap-1.5 border ${perm === p ? "bg-brand/15 border-brand/40 text-brand" : "border-rud text-sec"}`}><Icon size={13} /> {p}</button>
          ))}
        </div>
        {link ? (
          <div className="flex items-center gap-2 border border-rud rounded-lg px-3 py-2" data-testid="share-link">
            <input readOnly value={link} className="bg-transparent flex-1 text-xs outline-none text-sec" />
            <button data-testid="share-copy" onClick={copy} className="text-brand">{copied ? <Check size={15} /> : <Copy size={15} />}</button>
          </div>
        ) : (
          <button data-testid="share-create-link" onClick={makeLink} disabled={busy} className="btn-primary rounded-lg w-full h-10 text-sm flex items-center justify-center gap-2 disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <>Create share link</>}</button>
        )}
        <div className="flex items-center gap-2 mt-3">
          <Mail size={15} className="text-sec" />
          <input data-testid="share-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email it to…" className="rud-input flex-1 rounded-lg px-3 py-2 text-sm" />
          <button data-testid="share-email-send" onClick={sendEmail} disabled={busy || !email.trim()} className="btn-ghost rounded-lg px-3 py-2 text-sm disabled:opacity-60">Send</button>
        </div>
      </motion.div>
    </div>
  );
}
